import { BaseService } from "../../core/baseService";
import {
  ServiceCategoryDto,
  CreateServiceCategoryDto,
  UpdateServiceCategoryDto,
  ReorderServiceCategoriesDto,
} from "../types/serviceCategory.types";
import { ApiResult } from "../../core/types";

export interface ServiceCategoryServiceInterface {
  getAll(includeInactive?: boolean): Promise<ApiResult<ServiceCategoryDto[]>>;
  getById(id: string): Promise<ApiResult<ServiceCategoryDto>>;
  create(data: CreateServiceCategoryDto): Promise<ApiResult<ServiceCategoryDto>>;
  update(id: string, data: UpdateServiceCategoryDto): Promise<ApiResult<void>>;
  delete(id: string): Promise<ApiResult<void>>;
  reorder(data: ReorderServiceCategoriesDto): Promise<ApiResult<void>>;
  toggleActive(id: string, isActive: boolean): Promise<ApiResult<void>>;
  uploadCategoryImage(id: string, file: File): Promise<ApiResult<{ imageUrl: string }>>;
  removeCategoryImage(id: string): Promise<ApiResult<void>>;
}

export class ServiceCategoryService extends BaseService implements ServiceCategoryServiceInterface {
  async getAll(includeInactive = false): Promise<ApiResult<ServiceCategoryDto[]>> {
    return this.handleRequest<ServiceCategoryDto[]>(
      this.client.get("/api/admin/service-categories", {
        params: { includeInactive },
      })
    );
  }

  async getById(id: string): Promise<ApiResult<ServiceCategoryDto>> {
    return this.handleRequest<ServiceCategoryDto>(
      this.client.get(`/api/admin/service-categories/${id}`)
    );
  }

  async create(data: CreateServiceCategoryDto): Promise<ApiResult<ServiceCategoryDto>> {
    return this.handleRequest<ServiceCategoryDto>(
      this.client.post("/api/admin/service-categories", data)
    );
  }

  async update(id: string, data: UpdateServiceCategoryDto): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put(`/api/admin/service-categories/${id}`, data)
    );
  }

  async delete(id: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/service-categories/${id}`)
    );
  }

  async reorder(data: ReorderServiceCategoriesDto): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.put("/api/admin/service-categories/reorder", data)
    );
  }

  async toggleActive(id: string, isActive: boolean): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.patch(`/api/admin/service-categories/${id}/toggle`, null, {
        params: { isActive },
      })
    );
  }

  async uploadCategoryImage(id: string, file: File): Promise<ApiResult<{ imageUrl: string }>> {
    const formData = new FormData();
    formData.append("file", file);
    return this.handleRequest<{ imageUrl: string }>(
      this.client.post(`/api/admin/service-categories/${id}/image`, formData, {
        transformRequest: [(data: FormData, headers: Record<string, string>) => {
          delete headers["Content-Type"];
          return data;
        }],
      })
    );
  }

  async removeCategoryImage(id: string): Promise<ApiResult<void>> {
    return this.handleRequest<void>(
      this.client.delete(`/api/admin/service-categories/${id}/image`)
    );
  }
}
